import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  MessageCircleQuestion, RefreshCw, Send, Edit3, Check, X, 
  Sparkles, TrendingUp, AlertCircle, Clock, CheckCircle,
  Copy, ExternalLink, Trash2, Plus, BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GBPQAMonitor = () => {
  const [questions, setQuestions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [generating, setGenerating] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [newQuestion, setNewQuestion] = useState({ question_text: '', asked_by: 'Customer' });
  const [copied, setCopied] = useState('');
  const [seoKeywords, setSeoKeywords] = useState(null);

  const fetchQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const url = filter === 'all' 
        ? `${API_URL}/api/gbp-qa/questions`
        : `${API_URL}/api/gbp-qa/questions?status=${filter}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setQuestions(data.questions || []);
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Error fetching questions:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchSeoKeywords = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gbp-qa/seo-keywords`);
      if (res.ok) {
        const data = await res.json();
        setSeoKeywords(data);
      }
    } catch (err) {
      console.error('Error fetching SEO keywords:', err);
    }
  };

  useEffect(() => {
    fetchQuestions();
    fetchSeoKeywords();
  }, [fetchQuestions]);

  const regenerateAnswer = async (questionId) => {
    setGenerating(prev => ({ ...prev, [questionId]: true }));
    try {
      const res = await fetch(`${API_URL}/api/gbp-qa/questions/${questionId}/generate-answer`, {
        method: 'POST'
      });
      if (res.ok) {
        await fetchQuestions();
      }
    } catch (err) {
      console.error('Error regenerating answer:', err);
    } finally {
      setGenerating(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const saveAnswer = async (questionId, postToGbp = false) => {
    try {
      const res = await fetch(`${API_URL}/api/gbp-qa/questions/${questionId}/answer`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answer: editText, post_to_gbp: postToGbp })
      });
      if (res.ok) {
        setEditingId(null);
        setEditText('');
        await fetchQuestions();
      }
    } catch (err) {
      console.error('Error saving answer:', err);
    }
  };

  const updateStatus = async (questionId, status) => {
    try {
      await fetch(`${API_URL}/api/gbp-qa/questions/${questionId}/status?status=${status}`, {
        method: 'PUT'
      });
      await fetchQuestions();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const deleteQuestion = async (questionId) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await fetch(`${API_URL}/api/gbp-qa/questions/${questionId}`, {
        method: 'DELETE'
      });
      await fetchQuestions();
    } catch (err) {
      console.error('Error deleting question:', err);
    }
  };

  const addQuestion = async () => {
    if (!newQuestion.question_text.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/gbp-qa/questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newQuestion)
      });
      if (res.ok) {
        setShowAddModal(false);
        setNewQuestion({ question_text: '', asked_by: 'Customer' });
        await fetchQuestions();
      }
    } catch (err) {
      console.error('Error adding question:', err);
    }
  };

  const seedSampleQuestions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gbp-qa/seed-sample-questions`, {
        method: 'POST'
      });
      if (res.ok) {
        await fetchQuestions();
        alert('Sample questions added! AI is generating answers...');
      }
    } catch (err) {
      console.error('Error seeding questions:', err);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'answered': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'draft': return <Edit3 className="w-5 h-5 text-blue-500" />;
      case 'ignored': return <X className="w-5 h-5 text-gray-400" />;
      default: return <AlertCircle className="w-5 h-5 text-red-500" />;
    }
  };

  const getSeoScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    if (score >= 40) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  const getSeoGrade = (score) => {
    if (score >= 80) return 'A';
    if (score >= 60) return 'B';
    if (score >= 40) return 'C';
    return 'D';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      implants: '🦷 Implants',
      invisalign: '😁 Invisalign',
      cosmetic: '✨ Cosmetic',
      emergency: '🚨 Emergency',
      lanap: '🔬 LANAP',
      dentures: '🦷 Dentures',
      general: '📋 General'
    };
    return labels[category] || '📋 General';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>GBP Q&A Monitor | Admin Dashboard</title>
      </Helmet>

      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageCircleQuestion className="w-6 h-6 text-blue-600" />
            GBP Q&A Monitor
          </h1>
          <p className="text-gray-600 mt-1">
            Monitor and respond to customer questions on Google Business Profile with SEO-optimized answers
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4 text-center border-l-4 border-red-500">
              <p className="text-3xl font-bold text-red-600">{stats.unanswered}</p>
              <p className="text-sm text-gray-600">Unanswered</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center border-l-4 border-blue-500">
              <p className="text-3xl font-bold text-blue-600">{stats.draft}</p>
              <p className="text-sm text-gray-600">Drafts</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center border-l-4 border-green-500">
              <p className="text-3xl font-bold text-green-600">{stats.answered}</p>
              <p className="text-sm text-gray-600">Answered</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center border-l-4 border-purple-500">
              <p className="text-3xl font-bold text-purple-600">{stats.total}</p>
              <p className="text-sm text-gray-600">Total</p>
            </div>
            <div className={`bg-white rounded-lg shadow p-4 text-center border-l-4 ${
              stats.avg_seo_score >= 70 ? 'border-green-500' : 'border-yellow-500'
            }`}>
              <p className={`text-3xl font-bold ${stats.avg_seo_score >= 70 ? 'text-green-600' : 'text-yellow-600'}`}>
                {stats.avg_seo_score}%
              </p>
              <p className="text-sm text-gray-600">Avg SEO Score</p>
            </div>
          </div>
        )}

        {/* Action Bar */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex gap-2">
              {/* Filter Buttons */}
              {['all', 'unanswered', 'draft', 'answered'].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    filter === f 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                  {f === 'unanswered' && stats?.unanswered > 0 && (
                    <span className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                      {stats.unanswered}
                    </span>
                  )}
                </button>
              ))}
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition"
              >
                <Plus className="w-4 h-4" />
                Add Question
              </button>
              <button
                onClick={fetchQuestions}
                className="flex items-center gap-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              {questions.length === 0 && (
                <button
                  onClick={seedSampleQuestions}
                  className="flex items-center gap-1 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition"
                >
                  <Sparkles className="w-4 h-4" />
                  Load Sample Questions
                </button>
              )}
            </div>
          </div>
        </div>

        {/* SEO Keywords Reference */}
        {seoKeywords && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              SEO Keywords to Include in Answers
            </h3>
            <div className="flex flex-wrap gap-2">
              {seoKeywords.keywords?.primary?.slice(0, 8).map((kw, idx) => (
                <span key={idx} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  {kw}
                </span>
              ))}
              <span className="text-xs text-blue-600">+ service-specific keywords</span>
            </div>
          </div>
        )}

        {/* Questions List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            <p className="text-gray-500 mt-2">Loading questions...</p>
          </div>
        ) : questions.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <MessageCircleQuestion className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Questions Yet</h3>
            <p className="text-gray-500 mb-4">
              Add questions from your Google Business Profile or load sample questions to test the AI.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Add Question Manually
              </button>
              <button
                onClick={seedSampleQuestions}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Load Sample Questions
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {questions.map((q) => (
              <div 
                key={q.id} 
                className={`bg-white rounded-lg shadow overflow-hidden ${
                  q.status === 'unanswered' ? 'border-l-4 border-red-500' : ''
                }`}
              >
                {/* Question Header */}
                <div className="p-4 border-b bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {getStatusIcon(q.status)}
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          q.status === 'unanswered' ? 'bg-red-100 text-red-700' :
                          q.status === 'draft' ? 'bg-blue-100 text-blue-700' :
                          q.status === 'answered' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {q.status.toUpperCase()}
                        </span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                          {getCategoryLabel(q.category)}
                        </span>
                        {q.seo_score > 0 && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getSeoScoreColor(q.seo_score)}`}>
                            SEO: {q.seo_score}% ({getSeoGrade(q.seo_score)})
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">&ldquo;{q.question_text}&rdquo;</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Asked by {q.asked_by} • {new Date(q.asked_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => deleteQuestion(q.id)}
                        className="p-2 text-gray-400 hover:text-red-500 transition"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Answer Section */}
                <div className="p-4">
                  {editingId === q.id ? (
                    /* Edit Mode */
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Edit Answer (include SEO keywords!)
                      </label>
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full p-3 border rounded-lg text-sm min-h-[150px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Write your SEO-optimized answer..."
                      />
                      <div className="flex justify-between items-center mt-3">
                        <div className="text-sm text-gray-500">
                          {editText.split(' ').length} words • 
                          {editText.toLowerCase().includes('fort worth') ? ' ✓ Location' : ' ⚠ Add location'} • 
                          {editText.includes('817') ? ' ✓ Phone' : ' ⚠ Add phone'}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => { setEditingId(null); setEditText(''); }}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => saveAnswer(q.id, false)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                          >
                            Save Draft
                          </button>
                          <button
                            onClick={() => saveAnswer(q.id, true)}
                            className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                          >
                            <Send className="w-4 h-4" />
                            Mark as Posted
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <div>
                      {q.ai_suggested_answer || q.final_answer ? (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                              {q.final_answer ? '✅ Final Answer' : '🤖 AI Suggested Answer'}
                            </span>
                            <button
                              onClick={() => copyToClipboard(q.final_answer || q.ai_suggested_answer, q.id)}
                              className={`text-sm flex items-center gap-1 ${
                                copied === q.id ? 'text-green-600' : 'text-gray-500 hover:text-gray-700'
                              }`}
                            >
                              <Copy className="w-4 h-4" />
                              {copied === q.id ? 'Copied!' : 'Copy'}
                            </button>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4 text-gray-700">
                            {q.final_answer || q.ai_suggested_answer}
                          </div>
                          
                          {/* Keywords Used */}
                          {q.keywords_used?.length > 0 && (
                            <div className="mt-3">
                              <span className="text-xs text-gray-500">Keywords used: </span>
                              {q.keywords_used.map((kw, idx) => (
                                <span key={idx} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full mr-1">
                                  {kw}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="flex flex-wrap gap-2 mt-4">
                            <button
                              onClick={() => { setEditingId(q.id); setEditText(q.final_answer || q.ai_suggested_answer || ''); }}
                              className="flex items-center gap-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200"
                            >
                              <Edit3 className="w-4 h-4" />
                              Edit Answer
                            </button>
                            <button
                              onClick={() => regenerateAnswer(q.id)}
                              disabled={generating[q.id]}
                              className="flex items-center gap-1 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm hover:bg-purple-200 disabled:opacity-50"
                            >
                              {generating[q.id] ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Sparkles className="w-4 h-4" />
                              )}
                              Regenerate AI Answer
                            </button>
                            {q.status !== 'answered' && (
                              <button
                                onClick={() => updateStatus(q.id, 'answered')}
                                className="flex items-center gap-1 px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm hover:bg-green-200"
                              >
                                <CheckCircle className="w-4 h-4" />
                                Mark as Posted to GBP
                              </button>
                            )}
                            <a
                              href="https://business.google.com"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
                            >
                              <ExternalLink className="w-4 h-4" />
                              Open GBP to Post
                            </a>
                          </div>
                        </div>
                      ) : (
                        /* No Answer Yet */
                        <div className="text-center py-6">
                          <Clock className="w-8 h-8 mx-auto text-yellow-500 mb-2" />
                          <p className="text-gray-500 mb-3">AI is generating a suggested answer...</p>
                          <button
                            onClick={() => regenerateAnswer(q.id)}
                            disabled={generating[q.id]}
                            className="flex items-center gap-1 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 mx-auto disabled:opacity-50"
                          >
                            {generating[q.id] ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Sparkles className="w-4 h-4" />
                            )}
                            Generate AI Answer Now
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Question Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4">
              <h3 className="text-lg font-semibold mb-4">Add Question from GBP</h3>
              <p className="text-sm text-gray-500 mb-4">
                Copy a question from your Google Business Profile Q&A section and paste it here.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
                  <textarea
                    value={newQuestion.question_text}
                    onChange={(e) => setNewQuestion({ ...newQuestion, question_text: e.target.value })}
                    className="w-full p-3 border rounded-lg text-sm min-h-[100px]"
                    placeholder="e.g., How much would it cost for teeth whitening?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Asked By</label>
                  <input
                    type="text"
                    value={newQuestion.asked_by}
                    onChange={(e) => setNewQuestion({ ...newQuestion, asked_by: e.target.value })}
                    className="w-full p-3 border rounded-lg text-sm"
                    placeholder="e.g., Gilbert, Local Guide, Anonymous"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={addQuestion}
                  disabled={!newQuestion.question_text.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Add & Generate Answer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GBPQAMonitor;
